import React, { useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import ImageUsage from "./ImageUsage";
import ComputeUsage from "./ComputeUsage";
import { useParams } from "react-router-dom";
import QueueSize from "./QueueSize";
import Scheduling from "./Scheduling";
import HelpIcon from "@mui/icons-material/Help";
import { useSuspenseQuery } from "@connectrpc/connect-query";
import { insights } from "../api/v1/api-BackendService_connectquery";
import { Granularity, Platform } from "../api/v1/api_pb";
import moment from "moment/moment";
import Comparison from "./Comparison";
import { CIRRUS_RUNNERS_MONTHLY_PRICE, PLATFORMS } from "./constants";

const DAILY_GRANULARITY_DAYS = 90;
const BILLING_PERIOD_DAYS = 30;

function Insights() {
  const params = useParams();
  const [platforms, setPlatforms] = useState<Platform[]>(
    Array.from(PLATFORMS.keys()),
  );
  const [granularity, setGranularity] = useState<number>(Granularity.HOUR);
  const days = granularity === Granularity.DAY ? DAILY_GRANULARITY_DAYS : 1;
  const dateFormat = granularity === Granularity.HOUR ? "D MMM HH:mm" : "D MMM";

  const { data: dataHourly } = useSuspenseQuery(
    insights,
    {
      organization: params.organization,
      granularity: Granularity.HOUR,
    },
    {
      // Caching[1] and invalidation is out-of-scope at the moment,
      // so let's just disable the automatic refetch
      //
      // [1]: https://connectrpc.com/docs/go/get-requests-and-caching/
      refetchOnWindowFocus: false,
    },
  );
  const { data: dataDaily } = useSuspenseQuery(
    insights,
    {
      organization: params.organization,
      granularity: Granularity.DAY,
    },
    {
      // Caching[1] and invalidation is out-of-scope at the moment,
      // so let's just disable the automatic refetch
      //
      // [1]: https://connectrpc.com/docs/go/get-requests-and-caching/
      refetchOnWindowFocus: false,
    },
  );
  const data = granularity === Granularity.DAY ? dataDaily : dataHourly;

  const monthlyPrice = Number(data.runnersCount) * CIRRUS_RUNNERS_MONTHLY_PRICE;
  const computeMinutes = data.aggregates
    .flatMap((aggregate) => aggregate.platformMetrics)
    .filter((metrics) => platforms.includes(metrics.platform))
    .reduce((accumulator, metrics) => {
      return accumulator.add(
        metrics.computeUsageScaledSeconds?.sum ?? 0,
        "seconds",
      );
    }, moment.duration())
    .asMinutes();
  const ratio = BILLING_PERIOD_DAYS / days;
  const pricePerMinute =
    computeMinutes > 0 ? monthlyPrice / (computeMinutes * ratio) : null;

  return (
    <>
      <Stack alignItems="center" gap={4}>
        <Comparison data={dataDaily} periodDays={DAILY_GRANULARITY_DAYS} />
        <Alert severity="info" variant="outlined">
          Estimated price performance of Cirrus Runners for the last{" "}
          {days === 1 ? "day" : `${days} days`} is{" "}
          {pricePerMinute ? (
            <>
              ${pricePerMinute.toPrecision(2)} per minute
              <Tooltip title="Assumes annual commitment">
                <Typography
                  component="span"
                  color="info"
                  sx={{ cursor: "help", fontWeight: "bold" }}
                >
                  *
                </Typography>
              </Tooltip>
            </>
          ) : (
            "not available for the selected filters"
          )}
          . Please see charts below for more insights.
        </Alert>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: 240,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="platform-select-label">Platforms</InputLabel>
              <Select
                labelId="platform-select-label"
                multiple
                value={platforms}
                label="Platforms"
                input={<OutlinedInput label="Platforms" />}
                onChange={(e) => setPlatforms(e.target.value as Platform[])}
                renderValue={(selected) =>
                  selected
                    .map((platform: Platform) => PLATFORMS.get(platform)?.label)
                    .join(", ")
                }
              >
                {Array.from(PLATFORMS.entries()).map(
                  ([platform, platformDetails]) => (
                    <MenuItem key={platform} value={platform}>
                      <Checkbox
                        checked={platforms.includes(platform)}
                        size="small"
                      />
                      <ListItemText primary={platformDetails.label} />
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Box>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="granularity-label">Granularity</InputLabel>
            <Select
              labelId="granularity-label"
              value={granularity}
              label="Granularity"
              onChange={(e) => setGranularity(e.target.value)}
            >
              <MenuItem value={Granularity.DAY}>Daily</MenuItem>
              <MenuItem value={Granularity.HOUR}>Hourly</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6">Minutes Usage</Typography>
          <Tooltip title="Overall amount of minutes that Cirrus Runners were executing jobs">
            <HelpIcon />
          </Tooltip>
        </Stack>
        <ComputeUsage
          insights={data}
          dateFormat={dateFormat}
          platforms={platforms}
        />

        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6">Workflow Runs</Typography>
          <Tooltip title="Amount of jobs executed grouped by image used">
            <HelpIcon />
          </Tooltip>
        </Stack>
        <ImageUsage
          insights={data}
          dateFormat={dateFormat}
          platforms={platforms}
        />

        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6">Queue Size</Typography>
          <Tooltip title="Amount of jobs waiting while all Cirrus Runners are occupied with previous jobs">
            <HelpIcon />
          </Tooltip>
        </Stack>
        <QueueSize
          insights={data}
          dateFormat={dateFormat}
          platforms={platforms}
        />

        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6">Queueing Time</Typography>
          <Tooltip title="Duration that jobs waited in the queue while all Cirrus Runners were occupied with previous jobs. This is an impact of the queue size. Please consider increasing the amount of Cirrus Runners.">
            <HelpIcon />
          </Tooltip>
        </Stack>
        <Scheduling
          insights={data}
          dateFormat={dateFormat}
          platforms={platforms}
        />
      </Stack>
    </>
  );
}

export default Insights;
